import type { Insurance } from "@hermes/api";
import { useFlag } from "@hermes/flags";
import type { TimeSlot } from "@hermes/shared";
import {
  type FormErrorsContainer,
  useButtonFieldState,
  useCheckboxState,
  useFormFields,
  useSelectState,
  useTextFieldState,
} from "@hermes/ui";

export type AppointmentFormFields = ReturnType<typeof useAppointmentFormFields>;

export const useAppointmentFormFields = ({
  defaultService,
  initialSlot,
  errors,
  enableConsent,
  initialValues,
  phoneMask,
}: {
  defaultService?: number;
  initialSlot: TimeSlot | undefined;
  errors: FormErrorsContainer;
  enableConsent: boolean;
  initialValues?: {
    name?: string;
    phone?: string;
  };
  phoneMask: string;
}) => {
  const flag = useFlag();
  const isSurnameEnabled = flag({ id: "appointment-modal.enable-surname" });
  const cleanedPhoneMask = phoneMask
    .replace(/[^\d+#]/g, "")
    .replace(/(?!^)\+/g, "");

  return useFormFields({
    service: useSelectState({
      value: initialSlot?.slot.service ?? defaultService ?? null,
      required: true,
      validateInitially: true,
      error: errors.get("doctor_service"),
    }),
    slot: useButtonFieldState({
      value: initialSlot ?? null,
      required: true,
      disabled: !defaultService,
      validateInitially: true,
      error: errors.get("slot"),
    }),
    name: useTextFieldState({
      value: isSurnameEnabled
        ? (initialValues?.name?.split(" ")[0] ?? "")
        : (initialValues?.name ?? ""),
      required: true,
      validateInitially: Boolean(initialValues?.name),
      error: errors.get("client_name"),
    }),
    surname: useTextFieldState({
      value: isSurnameEnabled ? (initialValues?.name?.split(" ")[1] ?? "") : "",
      required: isSurnameEnabled,
      validateInitially: Boolean(initialValues?.name && isSurnameEnabled),
      error: errors.get("full_name"),
    }),
    phone: useTextFieldState({
      value: initialValues?.phone ?? "",
      required: true,
      validateInitially: Boolean(initialValues?.phone),
      rules: [(v) => cleanedPhoneMask.length === v.length],
      error: errors.get("client_phone"),
    }),
    consent: useCheckboxState({
      value: false,
      required: enableConsent,
      error: errors.get("consent"),
    }),
  });
};

export type AppointmentOtpFields = ReturnType<typeof useAppointmentOtpFields>;

export const useAppointmentOtpFields = (errors: FormErrorsContainer) => {
  return useFormFields(() => ({
    code: useTextFieldState({
      value: "",
      required: true,
      validateInitially: false,
      showValidationStatus: false,
      rules: [(value) => !value.includes("-")],
      error: errors.get("otp"),
    }),
  }));
};

export type AppointmentInsuranceFields = ReturnType<
  typeof useAppointmentInsuranceFields
>;

export const useAppointmentInsuranceFields = ({
  insurances,
}: {
  insurances: Insurance[];
}) => {
  return useFormFields(() => ({
    select: useSelectState({
      value: insurances[0] ?? null,
      required: true,
      validateInitially: false,
      showValidationStatus: false,
    }),
    checkbox: useCheckboxState({
      value: false,
    }),
  }));
};
